import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomeSlider from "@/components/HomeSlider";
import SmoothScroll from "@/components/SmoothScroll";
import NewsletterCTA from "@/components/NewsletterCTA";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowUpRight, Quote, ArrowLeft, ArrowRight } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

// "Three Approaches" — each card is a full image w/ overlay.
const TYPE_CARDS = [
  {
    key: "bespoke",
    image:
      "https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=1400&q=80",
  },
  {
    key: "adventure",
    image:
      "https://images.unsplash.com/photo-1518043610038-064362b44076?auto=format&fit=crop&w=1400&q=80",
  },
  {
    key: "cultural",
    image:
      "https://images.pexels.com/photos/19758828/pexels-photo-19758828.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400",
  },
];

// Featured destinations — Yogyakarta, Malang, Kulon Progo prioritized first.
const DESTINATIONS = [
  {
    name: "Yogyakarta",
    subtitle: "Court & Craft",
    url: "https://images.unsplash.com/photo-1555400038-63f5ba517a47?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    span: "md:col-span-7",
    priority: true,
  },
  {
    name: "Malang",
    subtitle: "Bromo & East Java",
    url: "https://images.unsplash.com/photo-1518043610038-064362b44076?auto=format&fit=crop&w=940&q=80",
    span: "md:col-span-5",
    priority: true,
  },
  {
    name: "Kulon Progo",
    subtitle: "Menoreh Tea Hills",
    url: "https://images.unsplash.com/photo-1683858222142-e5596d680fe3?auto=format&fit=crop&w=940&q=80",
    span: "md:col-span-5",
    priority: true,
  },
  {
    name: "Bali",
    subtitle: "Ricefields & Ceremony",
    url: "https://images.pexels.com/photos/35428411/pexels-photo-35428411.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940",
    span: "md:col-span-7",
  },
  {
    name: "Komodo",
    subtitle: "Dragons & Manta",
    url: "https://images.unsplash.com/photo-1604560929658-bbc3c2ba6a36?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    span: "md:col-span-6",
  },
  {
    name: "Raja Ampat",
    subtitle: "Four Kings",
    url: "https://images.pexels.com/photos/10482395/pexels-photo-10482395.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940",
    span: "md:col-span-6",
  },
];

const TESTIMONIALS = [
  {
    name: "Léa Marchand",
    role: "Paris, France",
    quote:
      "They redrew our entire two-week itinerary the night a volcano closed. We didn't know until the third day. Effortless.",
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&q=80",
  },
  {
    name: "Anders Holm",
    role: "Stockholm, Sweden",
    quote:
      "What I will always remember is the quiet. A Sumba dawn ride. They were already there before us.",
    avatar:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=80",
  },
  {
    name: "Mei Tanaka",
    role: "Tokyo, Japan",
    quote:
      "Booking a private temple ceremony in Ubud felt impossible. Their team makes the impossible feel ordinary.",
    avatar:
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=400&q=80",
  },
];

const PARTNER_CTA_IMAGE =
  "https://images.unsplash.com/photo-1516011362164-3095a82b0af9?auto=format&fit=crop&w=2400&q=80";

const TESTIMONIAL_BG =
  "https://images.unsplash.com/photo-1539367628448-4bc5c9d171c8?auto=format&fit=crop&w=2400&q=80";

const APPROACH_BG =
  "https://images.unsplash.com/photo-1518043610038-064362b44076?auto=format&fit=crop&w=2400&q=80";

export default function Landing() {
  const { t } = useTranslation();
  const root = useRef(null);
  const [activeT, setActiveT] = useState(0);

  const nextT = () => setActiveT((p) => (p + 1) % TESTIMONIALS.length);
  const prevT = () => setActiveT((p) => (p - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const current = TESTIMONIALS[activeT];

  // Animate the featured testimonial when it switches
  useEffect(() => {
    gsap.fromTo(
      ".testimonial-card",
      { opacity: 0, y: 18 },
      { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" }
    );
  }, [activeT]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray(".fade-up").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 36 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 88%" },
          }
        );
      });

      // Staggered reveal for grouped grids (destinations, testimonials)
      gsap.utils.toArray("[data-stagger-group]").forEach((group) => {
        const items = group.querySelectorAll("[data-stagger-item]");
        gsap.fromTo(
          items,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: 1.1,
            ease: "power3.out",
            stagger: 0.12,
            scrollTrigger: { trigger: group, start: "top 85%" },
          }
        );
      });

      // Headline reveals — subtle scale + fade for cinematic feel
      gsap.utils.toArray(".reveal-heading").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 40, scale: 0.98 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1.2,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 90%" },
          }
        );
      });

      // Parallax on cinematic backgrounds
      gsap.utils.toArray(".parallax-bg").forEach((el) => {
        gsap.fromTo(
          el,
          { y: -40, scale: 1.12 },
          {
            y: 40,
            scale: 1.12,
            ease: "none",
            scrollTrigger: {
              trigger: el,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          }
        );
      });

      // Type-card hover-friendly reveal stagger
      gsap.utils.toArray(".approach-card").forEach((el, i) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: 1.1,
            ease: "power3.out",
            delay: i * 0.12,
            scrollTrigger: { trigger: el, start: "top 90%" },
          }
        );
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <SmoothScroll>
      <div ref={root} className="min-h-screen bg-[#FDFBF7]">
        <Navbar transparent />
        <HomeSlider />

        {/* ──────────────── 1) Three Approaches — CINEMATIC dark ──────────────── */}
        <section
          className="relative bg-slate-950 overflow-hidden"
          data-testid="section-tour-types"
        >
          <div className="absolute inset-0 opacity-25">
            <img
              src={APPROACH_BG}
              alt=""
              aria-hidden
              className="parallax-bg absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/60 to-slate-950" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
            <div className="max-w-3xl">
              <div className="t-overline text-seafoam mb-5 fade-up">
                {t("types.eyebrow")}
              </div>
              <h2 className="text-white reveal-heading font-light tracking-tight leading-[0.95]"
                  style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)" }}>
                {t("types.title_a")}
                <br />
                <b className="font-bold">{t("types.title_b")}</b>
              </h2>
              <p className="mt-7 text-white/70 text-lg leading-relaxed max-w-xl fade-up">
                Three ways into Indonesia. Pick the one that fits the trip you
                wish to remember — or let our concierge braid all three into a
                single, perfectly paced itinerary.
              </p>
            </div>

            <div className="mt-20 grid md:grid-cols-3 gap-6 lg:gap-8">
              {TYPE_CARDS.map((tc) => (
                <Link
                  key={tc.key}
                  to="/tours"
                  data-testid={`type-card-${tc.key}`}
                  className="approach-card group relative overflow-hidden rounded-3xl aspect-[3/4] lg:aspect-[4/5] flex items-end"
                >
                  <img
                    src={tc.image}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-3xl" />

                  <div className="relative p-8 md:p-10 z-10 w-full">
                    <div className="t-overline text-seafoam mb-3">
                      0{TYPE_CARDS.indexOf(tc) + 1}
                    </div>
                    <h3 className="text-white text-3xl md:text-4xl font-light tracking-tight leading-tight">
                      <b className="font-bold">{t(`types.${tc.key}_title`)}</b>
                    </h3>
                    <p className="mt-4 text-white/80 leading-relaxed text-[15px] max-w-xs">
                      {t(`types.${tc.key}_body`)}
                    </p>
                    <div className="mt-7 inline-flex items-center gap-2 text-sm text-white/90 group-hover:text-seafoam transition-colors">
                      <span>Discover</span>
                      <ArrowUpRight
                        size={16}
                        strokeWidth={1.6}
                        className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ──────────────── 2) Destinations Bento ──────────────── */}
        <section
          className="max-w-7xl mx-auto px-6 md:px-12 py-32"
          data-testid="section-destinations"
        >
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <div className="fade-up">
              <div className="t-overline text-seafoam mb-4">
                {t("destinations.eyebrow")}
              </div>
              <h2 className="t-h2 text-slate-900 max-w-2xl reveal-heading">
                We start in Java &mdash;<br />
                <b>then go everywhere.</b>
              </h2>
              <p className="mt-5 text-slate-600 max-w-xl leading-relaxed">
                Our home is Yogyakarta. Our priority routes today are Jogja,
                Malang and Kulon Progo &mdash; quietly expanding across the
                archipelago as our team grows.
              </p>
            </div>
            <Link
              to="/tours"
              data-testid="link-all-tours"
              className="fade-up inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 group"
            >
              {t("destinations.see_all")}
              <ArrowUpRight
                size={16}
                strokeWidth={1.6}
                className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </Link>
          </div>

          <div className="grid md:grid-cols-12 gap-5 md:gap-6" data-stagger-group>
            {DESTINATIONS.map((d, i) => (
              <Link
                key={d.name}
                to="/tours"
                data-stagger-item
                data-testid={`dest-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group relative overflow-hidden h-[300px] md:h-[460px] rounded-3xl ${d.span}`}
              >
                <div
                  className={`absolute inset-0 ${
                    i % 2 === 0 ? "paper-tear-card" : "paper-tear-strip"
                  } paper-grain`}
                >
                  <img
                    src={d.url}
                    alt={d.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
                </div>

                {d.priority && (
                  <div className="absolute top-5 left-5 z-10">
                    <span className="px-3 py-1 rounded-full bg-seafoam text-white text-[10px] font-semibold uppercase tracking-[0.2em] shadow-lg">
                      Priority route
                    </span>
                  </div>
                )}

                <div className="absolute bottom-7 left-7 right-7 text-white z-10">
                  <div className="t-overline opacity-90">{d.subtitle}</div>
                  <div className="text-3xl md:text-5xl font-light mt-2 tracking-tight leading-none">
                    <b className="font-bold">{d.name}</b>
                  </div>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-[13px] opacity-80 group-hover:opacity-100 transition-opacity">
                    Explore tours
                    <ArrowUpRight size={14} strokeWidth={1.6} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ──────────────── 3) Testimonials — Full-bleed cinematic ──────────────── */}
        <section
          className="relative overflow-hidden"
          data-testid="section-testimonials"
        >
          <div className="absolute inset-0">
            <img
              src={TESTIMONIAL_BG}
              alt=""
              aria-hidden
              className="parallax-bg absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
            <div className="max-w-3xl">
              <div className="t-overline text-seafoam mb-5 fade-up">
                {t("testimonials.eyebrow")}
              </div>
              <h2
                className="text-white font-light tracking-tight leading-[0.95] reveal-heading"
                style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.25rem)" }}
              >
                {t("testimonials.title_a")}{" "}
                <b className="font-bold">{t("testimonials.title_b")}</b>
              </h2>
              <p className="mt-7 text-white/75 text-lg leading-relaxed max-w-2xl fade-up">
                Three travelers, three quiet trips. None of them planned the
                same itinerary — all of them came home unhurried.
              </p>

              {/* Featured testimonial */}
              <div
                key={current.name}
                data-testid={`testimonial-${current.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="mt-14 testimonial-card"
              >
                <Quote
                  size={36}
                  strokeWidth={1.3}
                  className="text-seafoam mb-7"
                />
                <blockquote
                  className="text-white font-light tracking-tight leading-[1.25] max-w-3xl"
                  style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.25rem)" }}
                >
                  &ldquo;{current.quote}&rdquo;
                </blockquote>

                <div className="mt-10 flex items-center gap-5">
                  <img
                    src={current.avatar}
                    alt={current.name}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-seafoam/50"
                  />
                  <div>
                    <div className="text-base font-medium text-white">
                      {current.name}
                    </div>
                    <div className="text-xs text-white/55 tracking-[0.18em] uppercase mt-1">
                      {current.role}
                    </div>
                  </div>
                </div>
              </div>

              {/* Carousel controls — partner-CTA style, left-aligned */}
              <div className="mt-12 flex items-center gap-6 fade-up">
                <button
                  onClick={prevT}
                  data-testid="testimonial-prev"
                  aria-label="Previous testimonial"
                  className="w-12 h-12 rounded-full border border-white/25 text-white hover:bg-white hover:text-slate-900 hover:border-white transition-all duration-300 flex items-center justify-center"
                >
                  <ArrowLeft size={16} strokeWidth={1.7} />
                </button>
                <button
                  onClick={nextT}
                  data-testid="testimonial-next"
                  aria-label="Next testimonial"
                  className="w-12 h-12 rounded-full border border-white/25 text-white hover:bg-white hover:text-slate-900 hover:border-white transition-all duration-300 flex items-center justify-center"
                >
                  <ArrowRight size={16} strokeWidth={1.7} />
                </button>

                <div className="flex items-center gap-3 ml-4">
                  {TESTIMONIALS.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveT(i)}
                      data-testid={`testimonial-dot-${i}`}
                      aria-label={`Show testimonial ${i + 1}`}
                      className={`transition-all duration-500 rounded-full ${
                        i === activeT
                          ? "w-8 h-1.5 bg-seafoam"
                          : "w-1.5 h-1.5 bg-white/35 hover:bg-white/60"
                      }`}
                    />
                  ))}
                </div>

                <div className="ml-auto text-white/55 text-sm font-light tabular-nums">
                  <span className="text-white">
                    0{activeT + 1}
                  </span>
                  <span className="mx-2 text-white/30">/</span>
                  <span>0{TESTIMONIALS.length}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ──────────────── 4) Partner CTA — Full-bleed cinematic ──────────────── */}
        <section
          className="relative overflow-hidden"
          data-testid="section-partner-cta"
        >
          <div className="absolute inset-0">
            <img
              src={PARTNER_CTA_IMAGE}
              alt=""
              aria-hidden
              className="parallax-bg absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/30" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
            <div className="max-w-2xl fade-up">
              <div className="t-overline text-seafoam mb-5">
                {t("partner_cta.eyebrow")}
              </div>
              <h2
                className="text-white font-light tracking-tight leading-[0.95] reveal-heading"
                style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.25rem)" }}
              >
                {t("partner_cta.title_a")}{" "}
                <b className="font-bold">{t("partner_cta.title_b")}</b>
              </h2>
              <p className="mt-7 text-white/80 text-lg leading-relaxed">
                {t("partner_cta.body")}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/partner"
                  data-testid="cta-partner-apply"
                  className="btn-pill bg-white text-slate-900 hover:bg-stone-100 text-sm shadow-2xl"
                >
                  {t("partner_cta.cta")}
                  <ArrowUpRight size={16} strokeWidth={1.6} />
                </Link>
                <Link
                  to="/about"
                  className="btn-pill border border-white/30 text-white hover:bg-white/10 text-sm"
                >
                  Learn more about us
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ──────────────── 5) Journal Newsletter ──────────────── */}
        <div className="pt-12 md:pt-16 bg-[#FDFBF7]">
          <NewsletterCTA source="landing" />
        </div>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
